/**
 * Emerald Books — Multi-branch / multi-entity.
 *
 * Full CRUD: create, edit, activate/deactivate, delete.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Pencil, Building, Search, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { booksClient, errMsg, stripEmpty } from './api';

const empty = () => ({ code: '', name: '', address: '', country_code: '', default_currency: '', is_active: true });

const BranchesPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  const load = async () => {
    setLoading(true);
    try { const { data } = await booksClient.get('/branches'); setRows(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => [r.code, r.name, r.address, r.country_code].filter(Boolean).some((v) => String(v).toLowerCase().includes(s)));
  }, [rows, q]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = stripEmpty(form);
      payload.is_active = !!form.is_active;
      if (editing) {
        const patch = { ...payload };
        delete patch.code;
        await booksClient.patch(`/branches/${editing.branch_id}`, patch);
        toast.success('Branch updated');
      } else {
        await booksClient.post('/branches', payload);
        toast.success('Branch created');
      }
      setShowForm(false); setEditing(null); setForm(empty()); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const del = async (r) => {
    if (!confirm(`Delete branch ${r.code}?`)) return;
    try { await booksClient.delete(`/branches/${r.branch_id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({ ...empty(), ...r });
    setShowForm(true);
  };

  const toggleActive = async (r) => {
    try { await booksClient.patch(`/branches/${r.branch_id}`, { is_active: !r.is_active }); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="mx-auto max-w-5xl" data-testid="books-branches-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Branches</h1>
          <p className="mt-1 text-sm text-slate-500">Tag documents, journals and reports with a branch. Perfect for multi-location entities.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <Input placeholder="Search…" className="pl-7" value={q} onChange={(e) => setQ(e.target.value)} data-testid="branch-search" />
          </div>
          <Button onClick={() => { setShowForm(!showForm); setEditing(null); setForm(empty()); }} className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="branch-new-btn">
            <Plus className="mr-1 h-4 w-4" /> New branch
          </Button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mb-6 rounded-xl border bg-white p-5 shadow-sm" data-testid="branch-form">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Code</div>
              <Input required disabled={!!editing} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="LON" data-testid="branch-code" />
            </label>
            <label className="text-sm md:col-span-2">
              <div className="mb-1 text-xs text-slate-500">Name</div>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="London HQ" data-testid="branch-name" />
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Country code</div>
              <Input value={form.country_code} onChange={(e) => setForm({ ...form, country_code: e.target.value.toUpperCase() })} placeholder="GB" />
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-slate-500">Default currency</div>
              <Input value={form.default_currency} onChange={(e) => setForm({ ...form, default_currency: e.target.value.toUpperCase() })} placeholder="GBP" />
            </label>
            <label className="text-sm md:col-span-3">
              <div className="mb-1 text-xs text-slate-500">Address</div>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </label>
            <label className="flex items-center gap-2 text-sm md:col-span-4">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              <span>Active</span>
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</Button>
            <Button type="submit" className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="branch-save">Save</Button>
          </div>
        </form>
      )}

      <div className="rounded-xl border bg-white">
        {loading ? <div className="p-6 text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading…</div> :
          filtered.length === 0 ? <div className="p-10 text-center text-slate-400">No branches yet.</div> :
          <table className="w-full text-sm">
            <thead className="border-b bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="p-3 text-left">Code</th>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Country</th>
                <th className="p-3 text-left">Currency</th>
                <th className="p-3 text-left">Address</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.branch_id} className="border-b last:border-b-0" data-testid={`branch-row-${r.code}`}>
                  <td className="p-3 font-mono text-xs">{r.code}</td>
                  <td className="p-3 font-medium">{r.name}</td>
                  <td className="p-3">{r.country_code || '—'}</td>
                  <td className="p-3">{r.default_currency || '—'}</td>
                  <td className="p-3 text-slate-500 truncate max-w-xs">{r.address || '—'}</td>
                  <td className="p-3">
                    <button onClick={() => toggleActive(r)} className={'rounded-full px-2 py-0.5 text-xs ' + (r.is_active ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-slate-100 text-slate-500 hover:bg-slate-200')} data-testid={`branch-toggle-${r.code}`}>
                      {r.is_active ? 'active' : 'inactive'}
                    </button>
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => openEdit(r)} className="rounded p-1 text-slate-500 hover:text-emerald-600" title="Edit" data-testid={`branch-edit-${r.code}`}><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => del(r)} className="rounded p-1 text-slate-500 hover:text-rose-600" title="Delete" data-testid={`branch-delete-${r.code}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </div>
    </div>
  );
};

export default BranchesPage;
